import { useCallback, useState } from 'react'
import { Link, Outlet } from 'react-router-dom'
import { SideNav } from './SideNav'
import { TopBar } from './TopBar'
import { SettingsDialog } from '../pages/settings/SettingsDialog'
import { OpenSettingsContext } from '../pages/settings/useOpenSettings'

const NAV_KEY = 'bb.nav.open'

/* Layout shell for every /app route: fixed TopBar, fixed SideNav rail, and the
   page body offset to clear both.

   The chrome is BatchBooks; most page bodies below it are still the LedgerLift
   styling from app.css, which is why <main> keeps app.css's `.page` padding.
   The rail's collapsed state is remembered so it survives navigation and
   reloads.

   The Settings dialog lives here, once, and is raised through a context. As a
   route it navigated away from whatever the user was doing — abandoning an
   import half-way — and its Save button sat far down a long page. One instance
   at the shell level means every entry point (the profile menu, the wizard
   footers) opens the same dialog over the page instead of replacing it.

   What the dialog does *after* a save reaches the rest of the app through
   `settingsEvents` rather than another context: the import wizard is a route
   provider below this shell and cannot be handed a callback from here without
   threading it through the router. */
export function AppShell() {
  const [navOpen, setNavOpen] = useState(() => localStorage.getItem(NAV_KEY) === 'open')
  const [settingsOpen, setSettingsOpen] = useState(false)

  const toggleNav = useCallback(() => {
    setNavOpen((open) => {
      const next = !open
      localStorage.setItem(NAV_KEY, next ? 'open' : 'closed')
      return next
    })
  }, [])

  const openSettings = useCallback(() => setSettingsOpen(true), [])

  return (
    <OpenSettingsContext.Provider value={openSettings}>
      <div className="bb-appwrap">
        <TopBar />
        <SideNav open={navOpen} onToggle={toggleNav} />

        <main className={navOpen ? 'view page bb-app-main bb-app-main-wide' : 'view page bb-app-main'}>
          <Outlet />
        </main>

        <footer className={navOpen ? 'bb bb-appfoot bb-appfoot-wide' : 'bb bb-appfoot'}>
          <div className="bb-appfoot-in">
            <span>© {new Date().getFullYear()} BatchBooks Professional. All rights reserved.</span>
            {/* Support, Terms and Privacy appear in the prototypes but have no
                pages yet, so only destinations that exist are linked. */}
            <nav className="bb-appfoot-links">
              <Link to="/#faq">Help Centre</Link>
              <Link to="/#pricing">Plans</Link>
            </nav>
          </div>
        </footer>

        <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>
    </OpenSettingsContext.Provider>
  )
}
